import { EntityType_PEnum, ProfileItemType_PEnum, ProfileItemUnitOfArea_PEnum, ProfileItemUnitOfDistance_PEnum, TranslationFieldType_PEnum } from '../../../configs/pseudoEnums'
import { TranslatableText } from '../../../utility/TranslatableText'
import { removeObjectArrayElements, sortObjectArray, updateOrAddItem } from '../../../utility/Utils'

// ** Initial State
const initialState = {
  profileItems: [],
  selectedProfileItem: null,
  fullProfileItems: [],
  isValidated: false,
  isLoading: true,
  isProfileItemLoading: false,
  isSaving: false,
  isSubmitting: false,
  currentEditorLanguageCodes: { 0: 'en' },   //Allows us to set the selected language tab from outside of the TranslatableTextEditor component. An optional key can be supplied to the TranslatableTextEditor if there is more than one TranslatableTextEditor on a page, so that changing the language tab on one does not affect the other.
  tags: {},
  tagsLoading: false
}

export const getDefaultProfileItem = (profileId, profileItemType) => {
  return {
    id: '',
    features: [],
    name: TranslatableText.getNew(true, '', TranslationFieldType_PEnum.Name),
    configuration: '',
    description: TranslatableText.getNew(true, '', TranslationFieldType_PEnum.description),
    entityType: EntityType_PEnum.profileItem,
    isChanged: false,
    isEditing: false,   //Set to true for item currently edited in the overlay (so we can match the DB version with the local version after save)
    media: [],
    profileId,
    sequence: 0,
    type: profileItemType,
    count: 1,
    size: 0,
    units: profileItemType === ProfileItemType_PEnum.venue ? ProfileItemUnitOfDistance_PEnum.metres : ProfileItemUnitOfArea_PEnum.squareMetres,
    existsInDb: false,
    inclusionType: '',   //activities-only property
    affiliateCode: '', //sleeping arrangement only property
    tags: [] // Array of tag objects: { id, isSelected, selectedMonths }
  }
}

const profileItemReducer = (state = initialState, action) => {
  switch (action.type) {
    case 'GET_PROFILEITEM':
      return { ...state, ...state.profileItems.find(x => x.id === action.data) }
    case 'GET_PROFILEITEMS':
      return { ...state, profileItems: action.data }
    case 'SET_CURRENT_EDITOR_LANGUAGE_CODE':
      return { ...state, currentEditorLanguageCodes: action.data }
    case 'SET_IS_VALIDATED':
      return { ...state, profile: { ...state.profileItems, isValidated: action.data } }
    case 'SET_IS_LOADING':
      return { ...state, profile: { ...state.profileItems, isLoading: action.data } }
    case 'SET_IS_SUBMITTING':
      return { ...state, profile: { ...state.profileItems, isSubmitting: action.data } }
    case 'DELETE_PROFILEITEM':
      const deleteIndex = state.profileItems.findIndex(x => x.id === action.data)
      if (deleteIndex >= 0) {
        const copy = _.cloneDeep(state.profileItems)
        copy.splice(deleteIndex, 1)
        //The cloneDeep is needed. Otherwise dependent useEffects will not fire. Even though an item has been deleted, and redux state updates!
        return { ...state, profileItems: copy }
      }
    case 'SET_PROFILEITEM':
      return {
        ...state,
        profileItems: sortObjectArray(updateOrAddItem(state.profileItems, action.data, 'id'), 'sequence')
      }
    case 'CLEAR_SELECTED_PROFILEITEM': {
      return { ...state, selectedProfileItem: null }
    }
    case 'UPDATE_PROFILE_ITEM_THUMBNAIL': {
      const selectedProfileItemClone = _.cloneDeep(state.selectedProfileItem)
      const profileItemsClone = _.cloneDeep(state.profileItems)

      if (selectedProfileItemClone.media.length) {
        const profileItem = profileItemsClone.find(x => x.id === selectedProfileItemClone.id)
        profileItem.thumbnailBlobRelativePath = selectedProfileItemClone.thumbnailBlobRelativePath
      }

      return { ...state, profileItems: profileItemsClone }
    }
    case 'SET_LOADING_PROFILE_ITEM': {
      return { ...state, isProfileItemLoading: action.data }
    }
    case 'SET_SELECTED_PROFILE_ITEM': {
      return { ...state, selectedProfileItem: action.data }
    }
    case 'SET_PROFILEITEMS':
      return {
        ...state, profileItems: sortObjectArray(action.data, 'sequence'), isLoading: false, isSaving: false, isSubmitting: false
      }
    case 'SET_FULL_PROFILEITEMS':
      return { ...state, fullProfileItems: action.data, isLoading: false, isSaving: false, isSubmitting: false }
    case 'DELETE_PROFILEITEM_MEDIAITEMS': {
      const selectedProfileItemClone = _.cloneDeep(state.selectedProfileItem)
      const profileItemsClone = _.cloneDeep(state.profileItems)

      if (selectedProfileItemClone) {
        const mediaItems = selectedProfileItemClone.media
        selectedProfileItemClone.media = removeObjectArrayElements(mediaItems, 'id', action.data.mediaItems)

        let thumbnailBlobRelativePath = ''
        if (selectedProfileItemClone.media.length) {
          thumbnailBlobRelativePath = selectedProfileItemClone.thumbnailBlobRelativePath = ''
        }
        const profileItem = profileItemsClone.find(x => x.id === selectedProfileItemClone.id)
        profileItem.thumbnailBlobRelativePath = thumbnailBlobRelativePath
      }
      return { ...state, profileItems: profileItemsClone }
    }
    case 'RESET_PROFILEITEMS':
      return {
        ...state,
        profileItems: initialState.profileItems,
        isValidated: initialState.isValidated,
        isLoading: initialState.isLoading,
        isSaving: initialState.isSaving,
        isSubmitting: initialState.isSubmitting
      }

    case 'GET_TAGS_PROFILE_ITEM_PENDING':
      return { ...state, tagsLoading: true, tags: {} }
    case 'GET_TAGS_PROFILE_ITEM_SUCCESS':
      const tags = {}

      Object.keys(action.data.profileTags).forEach((key) => {
        tags[key] = action.data.profileTags[key].map((t) => ({
          id: t.id,
          selectedMonths: t.selectedMonths,
          isLoading: false
        }))
      })
      return { ...state, tagsLoading: false, tags: tags }
    case 'GET_TAGS_PROFILE_ITEM_FAILURE':
      return { ...state, tagsLoading: false }

    case 'ADD_TAG_TO_PROFILE_ITEM_PENDING': {
      const profileItemId = action.data.profileItemId
      let tagsClone = _.cloneDeep(state.tags)

      let profileTagsClone = _.cloneDeep(tagsClone[profileItemId])
      if (!profileTagsClone) {
        profileTagsClone = []
      }

      profileTagsClone.push({ id: action.data.tag.id, isLoading: true })

      tagsClone = {
        ...tagsClone,
        [profileItemId]: [...profileTagsClone]
      }

      return { ...state, tags: tagsClone }
    }

    case 'ADD_TAG_TO_PROFILE_ITEM_SUCCESS': {
      const profileItemId = action.data.profileItemId
      let tagsClone = _.cloneDeep(state.tags)

      const profileTagsClone = _.cloneDeep(tagsClone[profileItemId])
      const tagIndex = profileTagsClone.findIndex((t) => t.id === action.data.tagId)

      if (tagIndex !== -1) {
        profileTagsClone[tagIndex].isLoading = false
      }

      tagsClone = {
        ...tagsClone,
        [profileItemId]: [...profileTagsClone]
      }

      return { ...state, tags: tagsClone }
    }

    case 'ADD_TAG_TO_PROFILE_ITEM_FAILED': {
      const profileItemId = action.data.profileItemId
      let tagsClone = _.cloneDeep(state.tags)

      const profileTagsClone = _.cloneDeep(tagsClone[profileItemId])
      const tagIndex = profileTagsClone.findIndex((t) => t.id === action.data.tagId)

      if (tagIndex !== -1) {
        profileTagsClone[tagIndex].isLoading = false
      }

      tagsClone = {
        ...tagsClone,
        [profileItemId]: [...profileTagsClone]
      }

      return { ...state, tags: tagsClone }
    }

    case 'REMOVE_TAG_TO_PROFILE_ITEM_PENDING': {
      const profileItemId = action.data.profileItemId
      let tagsClone = _.cloneDeep(state.tags)

      const profileTagsClone = _.cloneDeep(tagsClone[profileItemId])
      const tagIndex = profileTagsClone.findIndex((t) => t.id === action.data.tagId)

      if (tagIndex !== -1) {
        profileTagsClone[tagIndex].isLoading = true
      }

      tagsClone = {
        ...tagsClone,
        [profileItemId]: [...profileTagsClone]
      }

      return { ...state, tags: tagsClone }
    }
    case 'REMOVE_TAG_TO_PROFILE_ITEM_SUCCESS': {
      const profileItemId = action.data.profileItemId
      let tagsClone = _.cloneDeep(state.tags)

      const profileTagsClone = _.cloneDeep(tagsClone[profileItemId])
      const tagIndex = profileTagsClone.findIndex((t) => t.id === action.data.tagId)

      if (tagIndex !== -1) {
        profileTagsClone.splice(tagIndex, 1)
      }

      tagsClone = {
        ...tagsClone,
        [profileItemId]: [...profileTagsClone]
      }

      return { ...state, tags: tagsClone }
    }
    case 'REMOVE_TAG_TO_PROFILE_ITEM_FAILED': {
      const profileItemId = action.data.profileItemId
      let tagsClone = _.cloneDeep(state.tags)

      const profileTagsClone = _.cloneDeep(tagsClone[profileItemId])
      const tagIndex = profileTagsClone.findIndex((t) => t.id === action.data.tagId)

      if (tagIndex !== -1) {
        profileTagsClone[tagIndex].isLoading = false
        profileTagsClone[tagIndex].error = action.data.error
      }

      tagsClone = {
        ...tagsClone,
        [profileItemId]: [...profileTagsClone]
      }

      return { ...state, tags: tagsClone }
    }

    case 'ADD_TAG_TO_PROFILE_ITEM_LOCAL': {
      const profileItemId = action.data.profileItemId
      let tagsClone = _.cloneDeep(state.tags)

      let profileTagsClone = _.cloneDeep(tagsClone[profileItemId])
      if (!profileTagsClone) {
        profileTagsClone = []
      }

      profileTagsClone.push({ id: action.data.tagId, selectedMonths: action.data.selectedMonths, toBeAdded: true })

      tagsClone = {
        ...tagsClone,
        [profileItemId]: [...profileTagsClone]
      }
      return { ...state, tags: tagsClone }
    }
    case 'REMOVE_TAG_TO_PROFILE_ITEM_LOCAL': {
      const profileItemId = action.data.profileItemId
      let tagsClone = _.cloneDeep(state.tags)

      const profileTagsClone = _.cloneDeep(tagsClone[profileItemId])
      const tagIndex = profileTagsClone.findIndex((t) => t.id === action.data.tagId)
      if (tagIndex !== -1) {
        profileTagsClone.splice(tagIndex, 1)
      }

      tagsClone = {
        ...tagsClone,
        [profileItemId]: [...profileTagsClone]
      }

      return { ...state, tags: tagsClone }
    }

    case 'ADD_ALL_TAGS_TO_PROFILE_ITEM_PENDING': {
      const profileItemId = action.data.profileItemId
      let tagsClone = _.cloneDeep(state.tags)

      let profileTagsClone = _.cloneDeep(tagsClone[profileItemId])
      if (!profileTagsClone) {
        profileTagsClone = []
      }

      const profileItemTags = action.data.tags.map((t) => ({
        id: t.id,
        isLoading: true
      }))

      profileTagsClone.push(...profileItemTags)

      tagsClone = {
        ...tagsClone,
        [profileItemId]: [...profileTagsClone]
      }

      return { ...state, tags: tagsClone }
    }

    case 'ADD_ALL_TAGS_TO_PROFILE_ITEM_SUCCESS': {
      const profileItemId = action.data.profileItemId
      const oldProfileItemId = 0 // id of profile item from which tags are being moved (new profile item id)
      let tagsClone = _.cloneDeep(state.tags)

      let profileTagsClone = _.cloneDeep(tagsClone[oldProfileItemId])
      if (!profileTagsClone) {
        profileTagsClone = []
      }

      profileTagsClone.forEach((tag) => {
        tag.isLoading = false
      })

      tagsClone = {
        ...tagsClone,
        [profileItemId]: [...profileTagsClone]
      }

      // Remove tags from old profile item
      delete tagsClone[oldProfileItemId]

      return { ...state, tags: tagsClone }
    }

    case 'ADD_ALL_TAGS_TO_PROFILE_ITEM_FAILED': {
      const profileItemId = action.data.profileItemId
      let tagsClone = _.cloneDeep(state.tags)

      const profileTagsClone = _.cloneDeep(tagsClone[profileItemId])

      profileTagsClone.forEach((tag) => {
        tag.isLoading = true
      })


      tagsClone = {
        ...tagsClone,
        [profileItemId]: [...profileTagsClone]
      }

      return { ...state, tags: tagsClone }
    }

    case 'UPDATE_TAGS_TO_PROFILE_ITEM_PENDING': {
      const profileItemId = action.data.profileItemId
      let tagsClone = _.cloneDeep(state.tags)

      let profileTagsClone = _.cloneDeep(tagsClone[profileItemId])
      if (!profileTagsClone) {
        profileTagsClone = []
      }

      const profileItemTags = action.data.tags
        .filter((t) => !profileTagsClone.find(existing => existing.id === t.id))
        .map((t) => ({
          id: t.id,
          isLoading: true
        }))

      profileTagsClone.push(...profileItemTags)

      tagsClone = {
        ...tagsClone,
        [profileItemId]: [...profileTagsClone]
      }

      return { ...state, tags: tagsClone }
    }

    case 'UPDATE_TAGS_TO_PROFILE_ITEM_SUCCESS': {
      const profileItemId = action.data.profileItemId
      let tagsClone = _.cloneDeep(state.tags)

      let profileTagsClone = _.cloneDeep(tagsClone[profileItemId])
      if (!profileTagsClone) {
        profileTagsClone = []
      }

      profileTagsClone.forEach((tag) => {
        tag.isLoading = false
      })

      tagsClone = {
        ...tagsClone,
        [profileItemId]: [...profileTagsClone]
      }

      return { ...state, tags: tagsClone }
    }

    case 'UPDATE_TAGS_TO_PROFILE_ITEM_FAILED': {
      const profileItemId = action.data.profileItemId
      let tagsClone = _.cloneDeep(state.tags)

      const profileTagsClone = _.cloneDeep(tagsClone[profileItemId])

      profileTagsClone.forEach((tag) => {
        tag.isLoading = true
      })


      tagsClone = {
        ...tagsClone,
        [profileItemId]: [...profileTagsClone]
      }

      return { ...state, tags: tagsClone }
    }
    default:
      return state
  }
}

export default profileItemReducer
